import useChartSelections from "components/charting/hooks/useChartSelections";
import { ProfilingChartEntityId } from "components/charting/types";
import { ChartLine } from "components/charting/types/Chart.types";
import LoadVelocityProfileHelpers from "helpers/LoadVelocityProfileHelpers";
import LineHelpers from "helpers/LineHelpers";
import { useMemo } from "react";
import type { Exercise, Variants } from "types";
import type { LoadVelocityProfile } from "types/load-velocity-profile";

export interface OneRepMaxEstimate {
  /** The side of the chart the estimate belongs to: the baseline or the comparison. */
  entityId: ProfilingChartEntityId;
  estimatedOneRepMax: number;
  /** The line's velocity at the estimated 1RM, where the estimate sits on the line. */
  estimatedVelocity: number;
  minimumVelocityThreshold: number;
  /** The athlete's own MVT, set when they lifted below the exercise's threshold and the profile's 1RM was raised. */
  impliedMvt?: number;
  /** Set when the estimate belongs to one side of a unilateral profile. */
  variant?: Variants;
}

const CHART_SIDES = [ProfilingChartEntityId.PROFILING_LINE, ProfilingChartEntityId.COMPARATIVE_LINE];

/**
 * Estimates each side's 1RM: from its profile, otherwise from the regression line of its sets.
 * Every estimate uses the exercise's threshold, which a custom exercise takes from the exercise it derives from.
 */
function useAthleteProfilingChartAnnotations(lines: ChartLine[]): OneRepMaxEstimate[] {
  const selections = useChartSelections();
  const exerciseMvt = (selections.measures[0]?.metadata as Exercise | undefined)?.minimumVelocityThreshold;

  return useMemo(
    () =>
      CHART_SIDES.flatMap((entityId): OneRepMaxEstimate[] => {
        const sideLines = lines.filter((line) => line.entityId === entityId);
        const profiles = sideLines
          .map(LoadVelocityProfileHelpers.getLineLvp)
          .filter((lvp): lvp is LoadVelocityProfile => Boolean(lvp));

        if (profiles.length) {
          return profiles.flatMap((lvp) =>
            exerciseMvt && lvp.predictedOneRepMax > 0
              ? [
                  {
                    entityId,
                    estimatedOneRepMax: lvp.predictedOneRepMax,
                    // A raised prediction sits past where the profile crosses the threshold, so plot it on the profile.
                    estimatedVelocity: lvp.intercept + lvp.slope * lvp.predictedOneRepMax,
                    minimumVelocityThreshold: exerciseMvt,
                    impliedMvt: lvp.impliedAthleteMvt ?? undefined,
                    variant: lvp.variant || undefined,
                  },
                ]
              : []
          );
        }

        const regressionLine = (sideLines[0]?.line ?? []).flatMap((point) =>
          point.metadataType === "Exercise" && point.profiling
            ? [[point.profiling.x, point.profiling.y] as [number, number]]
            : []
        );
        const estimate = exerciseMvt ? LineHelpers.getXIntersect(regressionLine, exerciseMvt) : null;

        return exerciseMvt && estimate
          ? [
              {
                entityId,
                estimatedOneRepMax: estimate,
                estimatedVelocity: exerciseMvt,
                minimumVelocityThreshold: exerciseMvt,
                variant: sideLines[0]?.variant,
              },
            ]
          : [];
      }),
    [exerciseMvt, lines]
  );
}

export default useAthleteProfilingChartAnnotations;
